/**
 * Hover preview of an event (Outlook/Google peek): title, time, recurrence, location, calendar, organizer, the
 * answer buttons of an invitation and "Open details" (the event panel). Free/busy blocks show only their time.
 */
import { useI18n } from '@platform/i18n';
import { Badge, Button, CALENDAR_COLOR_CLASSES, cn } from '@platform/ui';
import { ClockIcon, MapPinIcon, RepeatIcon, UserIcon, VideoIcon } from 'lucide-react';
import { answerable } from '../actions/calendar-actions';
import { calendarName, RESPONSE_LABEL, type ShownEvent } from './events';
import { useCalendarFormat } from './format';
import { RsvpButtons } from './rsvp';

export function EventPreview({ item, onOpen }: { item: ShownEvent; onOpen: () => void }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const { event, calendar, grid } = item;
  return (
    <div className="flex flex-col gap-2 text-sm" aria-label={t('webmail.calendarUx.preview.label')}>
      <div className="flex items-start gap-2">
        <span aria-hidden className={cn('mt-1 size-3 shrink-0 rounded-sm border', CALENDAR_COLOR_CLASSES[grid.color].swatch)} />
        <h3 className="min-w-0 flex-1 font-semibold break-words">{grid.title}</h3>
      </div>
      <p className="flex items-center gap-2 text-muted-foreground">
        <ClockIcon aria-hidden className="size-4 shrink-0" />
        {format.range(grid.start, grid.end, grid.allDay)}
        {event.isRecurring && <RepeatIcon aria-hidden className="size-3.5" />}
      </p>
      {grid.detail && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <MapPinIcon aria-hidden className="size-4 shrink-0" />
          <span className="truncate">{grid.detail}</span>
        </p>
      )}
      {event.organizer && !event.isOrganizer && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <UserIcon aria-hidden className="size-4 shrink-0" />
          <span className="truncate">{event.organizer.name}</span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="muted">{calendarName(calendar, t)}</Badge>
        {event.isMeeting && !event.isOrganizer && <Badge variant="outline">{t(RESPONSE_LABEL[event.myResponse])}</Badge>}
      </div>
      {answerable(item) && (
        <div className="flex flex-wrap items-center gap-1.5">
          <RsvpButtons event={event} size="xs" />
        </div>
      )}
      <div className="flex justify-end gap-1.5">
        {event.onlineMeetingUrl && (
          <Button size="sm" asChild>
            <a href={event.onlineMeetingUrl} target="_blank" rel="noopener noreferrer">
              <VideoIcon />
              {t('webmail.calendarUx.join')}
            </a>
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onOpen}>
          {t('webmail.calendarUx.preview.openDetails')}
        </Button>
      </div>
    </div>
  );
}
